import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ContactInquiry {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  ip_address: string | null;
  created_at: string;
}

interface Meta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  has_more: boolean;
}

async function fetchInquiries(page: number) {
  const res = await apiClient.get('/contact-inquiries', {
    params: { page, per_page: 15 },
  });
  return res.data;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ContactInquiriesPage() {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ContactInquiry | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-contact-inquiries', page],
    queryFn: () => fetchInquiries(page),
  });

  const inquiries: ContactInquiry[] = data?.data?.inquiries ?? [];
  const meta: Meta | undefined = data?.meta;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Contact Inquiries</h1>
        <p className="text-sm text-muted-foreground">
          Messages submitted via the public contact form
        </p>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Subject</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : inquiries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No contact inquiries found
                </td>
              </tr>
            ) : (
              inquiries.map((inquiry) => (
                <tr key={inquiry.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-medium">{inquiry.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inquiry.email}</td>
                  <td className="px-4 py-3 max-w-xs truncate" title={inquiry.subject}>
                    {inquiry.subject}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {formatDate(inquiry.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelected(inquiry)}
                      className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {meta.current_page} of {meta.last_page} ({meta.total} total)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!meta.has_more}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contact Inquiry</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                <span className="font-medium text-muted-foreground">Name</span>
                <span className="col-span-2">{selected.name}</span>

                <span className="font-medium text-muted-foreground">Email</span>
                <span className="col-span-2 break-all">{selected.email}</span>

                <span className="font-medium text-muted-foreground">Subject</span>
                <span className="col-span-2">{selected.subject}</span>

                <span className="font-medium text-muted-foreground">Date</span>
                <span className="col-span-2">{formatDate(selected.created_at)}</span>
              </div>

              <div>
                <p className="font-medium text-muted-foreground mb-1.5">Message</p>
                <div className="rounded-lg border bg-muted/30 p-3 whitespace-pre-wrap leading-relaxed">
                  {selected.message}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
